/**
 * Stock Management UI types.
 * Transfers tab is wired to the real backend (stock_transfers table).
 * Dashboard / BatchExpiry / HoldReserve tabs are visual scaffolds for
 * concepts (warehouse capacity, lot-level holds) that are deferred to a
 * future phase — they currently render empty state.
 */

export type StockTab = 'dashboard' | 'transfers' | 'adjustments' | 'batchExpiry' | 'holdReserve';

export interface StockStatItem {
  label: string;
  value: string;
  badge: string;
  badgeColor: string;
  iconBg: string;
  iconColor: string;
}

export interface WarehouseItem {
  name: string;
  type: string;
  barColor: string;
  skus: number;
  units: string;
  value: string;
  capacity: number;
}

export interface StockAlert {
  id: string;
  severity: 'critical' | 'warning' | 'expiring' | 'info';
  message: string;
  location: string;
  time: string;
}

export type TransferStatus = 'In Transit' | 'Complete' | 'Cancelled';

export interface TransferItem {
  id: string;
  date: string;
  from: string;
  to: string;
  items: string;
  units: number;
  value: string;
  status: TransferStatus;
  eta: string;
}

export type BatchStatus = 'Expiring' | 'Exp Soon' | 'Healthy';

export interface BatchItem {
  batchId: string;
  sku: string;
  product: string;
  warehouse: string;
  qty: number;
  received: string;
  expiryDate: string;
  daysLeft: string;
  fefo: string;
  status: BatchStatus;
}

export interface HoldItem {
  holdId: string;
  sku: string;
  product: string;
  warehouse: string;
  qtyHeld: number;
  reason: string;
  placedBy: string;
  date: string;
}

export type ReserveStatus = 'Picking' | 'Out for Delivery' | 'Approved' | 'Draft';

export interface ReserveItem {
  order: string;
  customer: string;
  skusReserved: number;
  totalUnits: number;
  warehouse: string;
  status: ReserveStatus;
}

export const holdReasonColors: Record<string, string> = {
  'Quality Review': '#D97706',
  Recall: '#DC2626',
};

export const reserveStatusColors: Record<ReserveStatus, string> = {
  Picking: '#059669',
  'Out for Delivery': '#2563EB',
  Approved: '#059669',
  Draft: '#6B7280',
};

export const transferStatusColors: Record<TransferStatus, { color: string; bg: string }> = {
  'In Transit': { color: '#2563EB', bg: '#EFF6FF' },
  Complete: { color: '#059669', bg: '#ECFDF5' },
  Cancelled: { color: '#DC2626', bg: '#FEF2F2' },
};

export const batchStatusColors: Record<BatchStatus, string> = {
  Expiring: '#DC2626',
  'Exp Soon': '#D97706',
  Healthy: '#059669',
};
